import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Copy, Check } from "lucide-react";
import { adminApi } from "@/lib/api";
import { formatApiError } from "@/admin/AdminAuthContext";
import { PageHeader, LoadingRow } from "@/admin/pages/AdminSubsidiaries";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("general");
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/media");
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      try {
        await adminApi.post("/admin/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Uploaded ${file.name}`);
      } catch (err) { toast.error(`${file.name}: ${formatApiError(err)}`); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await adminApi.delete(`/admin/media/${id}`);
      toast.success("Deleted.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyPath = async (id, path) => {
    try {
      await navigator.clipboard.writeText(path);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (_e) {
      toast.error("Clipboard unavailable — copy the path manually.");
    }
  };

  return (
    <div data-testid="admin-media-page">
      <PageHeader title="Media Library" />

      <div className="bg-white border-2 border-dashed border-softgray p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-mutedink mb-2">Category</div>
            <input
              type="text" value={category} onChange={(e) => setCategory(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ""))}
              className="w-full border border-softgray px-3 py-2 text-navy text-sm"
              placeholder="e.g. hero, subsidiaries, downloads"
              data-testid="media-category-input"
            />
            <div className="text-mutedink text-[11px] mt-1">Images (PNG/JPG/WEBP/SVG/GIF) and PDF, up to 15MB each.</div>
          </div>
          <label className="btn-gold cursor-pointer justify-center" data-testid="media-upload-button">
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload files"}
            <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf"
                   onChange={(e) => onUpload(e.target.files)} className="hidden"
                   data-testid="media-file-input" disabled={uploading} />
          </label>
        </div>
      </div>

      {loading ? <LoadingRow /> : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m) => {
            const isImage = m.content_type?.startsWith("image/");
            return (
              <div key={m.id} className="bg-white border border-softgray p-3 group" data-testid={`media-item-${m.id}`}>
                <div className="aspect-square bg-offwhite mb-3 flex items-center justify-center border border-softgray overflow-hidden">
                  {isImage
                    ? <img src={`${BACKEND_URL}${m.path}`} alt={m.filename}
                           className="max-w-full max-h-full object-contain" loading="lazy" />
                    : <div className="text-mutedink text-xs uppercase tracking-widest">{m.content_type?.replace("application/", "") || "file"}</div>}
                </div>
                <div className="text-navy text-[12px] font-mono truncate mb-1">{m.filename}</div>
                <div className="text-mutedink text-[11px] mb-3">{m.category} · {Math.round((m.size || 0) / 1024)} KB</div>
                <div className="flex gap-2">
                  <button className="flex-1 text-[10px] uppercase tracking-widest border border-softgray py-1.5 hover:border-gold hover:text-gold"
                          onClick={() => copyPath(m.id, m.path)} data-testid={`copy-path-${m.id}`}>
                    {copiedId === m.id ? <><Check size={11} className="inline" /> Copied</> : <><Copy size={11} className="inline" /> Copy path</>}
                  </button>
                  <button className="text-[10px] border border-red-300 text-red-700 py-1.5 px-2 hover:bg-red-50"
                          onClick={() => remove(m.id)} data-testid={`delete-media-${m.id}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            );
          })}
          {items.length === 0 && <div className="col-span-full bg-white border border-softgray p-8 text-mutedink text-sm text-center">No media yet — upload files above.</div>}
        </div>
      )}
    </div>
  );
}
